import { c as _c } from "react/compiler-runtime";
import { Box, styled } from '@mui/material';
import { NativeVaultColor } from '@/theme/colorSchemes';
export const resolveSnapshotBgColor = (backgroundColor: string | null, isNative?: boolean): string | null => backgroundColor ?? (isNative ? NativeVaultColor : null);

/**
 * How completely a snapshot captured its subject. Named level to keep it distinct from the SDK's
 * SnapshotCoverage, which is the object-store byte breakdown rather than a verdict.
 */
export type SnapshotCoverageLevel = 'complete' | 'partial' | 'failed';
export const SnapshotIndicator = props => {
  const $ = _c(10);
  let t0;
  if ($[0] !== props.backgroundColor || $[1] !== props.isNative) {
    t0 = resolveSnapshotBgColor(props.backgroundColor, props.isNative);
    $[0] = props.backgroundColor;
    $[1] = props.isNative;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const bgColor = t0;
  if (props.coverage === "failed") {
    let t1;
    if ($[3] !== props.sx) {
      t1 = <FailedSnapshotIndicator sx={props.sx} />;
      $[3] = props.sx;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== bgColor || $[6] !== props.coverage || $[7] !== props.isPartial || $[8] !== props.sx) {
    t1 = props.coverage === "partial" || props.isPartial ? <PartialSnapshotIndicator backgroundColor={bgColor} sx={props.sx} /> : <FullSnapshotIndicator backgroundColor={bgColor} sx={props.sx} />;
    $[5] = bgColor;
    $[6] = props.coverage;
    $[7] = props.isPartial;
    $[8] = props.sx;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
const FullSnapshotIndicator = styled('div')((props: {
  backgroundColor: string | null;
  sx?: object;
}) => ({
  height: '12px',
  width: '12px',
  borderRadius: '50%',
  display: 'inline-block',
  minWidth: '12px',
  minHeight: '12px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: props.backgroundColor || 'gray',
  backgroundColor: props.backgroundColor || 'transparent',
  ...props.sx
}));

// Deliberately carries no vault colour. The other dots answer "where is this snapshot kept";
// this one answers "this backup failed", which the vault palette must not soften.
const FailedSnapshotIndicator = props => {
  const $ = _c(7);
  let t0;
  if ($[0] !== props.sx) {
    t0 = {
      height: "12px",
      width: "12px",
      minWidth: "12px",
      minHeight: "12px",
      display: "inline-block",
      ...props.sx
    };
    $[0] = props.sx;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="6" cy="6" r="5.5" fill="var(--mui-palette-background-paper)" stroke="var(--mui-palette-error-main)" />;
    t2 = <path d="M6 3.1V6.7" stroke="var(--mui-palette-error-main)" strokeWidth="1.4" strokeLinecap="round" />;
    t3 = <circle cx="6" cy="8.7" r="0.85" fill="var(--mui-palette-error-main)" />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0) {
    t4 = <Box component="svg" viewBox="0 0 12 12" role="img" aria-label="Failed" sx={t0}>{t1}{t2}{t3}</Box>;
    $[5] = t0;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const PartialSnapshotIndicator = props => {
  const $ = _c(8);
  const t0 = `2px solid ${props.backgroundColor}`;
  let t1;
  if ($[0] !== props.sx || $[1] !== t0) {
    t1 = {
      height: "12px",
      width: "12px",
      borderRadius: "50%",
      display: "inline-block",
      minWidth: "12px",
      minHeight: "12px",
      border: t0,
      overflow: "hidden",
      ...props.sx
    };
    $[0] = props.sx;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.backgroundColor) {
    t2 = <Box sx={{
      marginTop: "-1.5px",
      marginLeft: "-3px",
      height: "5px",
      width: "12px",
      transform: "rotate(-45deg)",
      backgroundColor: props.backgroundColor
    }} />;
    $[3] = props.backgroundColor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Box role="img" aria-label="Partial" sx={t1}>{t2}</Box>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};